// src/components/enterprise/AnonymousFeedback.jsx

import React, { useMemo, useState } from 'react';
import {
    MessageSquare, Sparkles, EyeOff, Users, ShieldCheck, Lock, Info
} from 'lucide-react';
import { useEdition } from '../../data/editions/EditionContext';

/* ------------------------------------------------------------------ */
/*  Privacy floor (Section 6): aggregate reporting is released only    */
/*  at a minimum group size. k >= 5 is the floor; 10 is recommended    */
/*  for larger employers. Anything below the floor is suppressed and   */
/*  rolled into the organization-wide view so no small group — and no  */
/*  individual — can be re-identified.                                 */
/* ------------------------------------------------------------------ */

const MIN_GROUP_SIZE = 5;
const RECOMMENDED_GROUP_SIZE = 10;

/* --- inline discipline atoms (consolidate into the shared layer later) --- */
const ConfidenceTag = ({ level = 'Leading' }) => {
    const map = {
        Leading: 'text-emerald-700 bg-emerald-50 border-emerald-100',
        Intermediate: 'text-[var(--brand-deep)] bg-blue-50 border-blue-100',
        Lagging: 'text-amber-700 bg-amber-50 border-amber-100'
    };
    return (
        <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${map[level]}`}>
            {level} indicator
        </span>
    );
};

const AnonymousFeedback = ({ live }) => {
    const [scope, setScope] = useState('org');

    // Scopes, pooled de-identified submissions and the sentiment summary all
    // come from the active edition. Quotes are NEVER shown with a group label;
    // the scope only pools submissions once it clears the k >= 5 floor.
    // T1.3 note: only the participation METRIC goes live here — there is no
    // live pulse-content pipeline yet, so scopes/submissions honestly remain
    // the modeled example until one exists (Phase F territory).
    const edition = useEdition();
    const SCOPES = edition.feedback.scopes;
    const SUBMISSIONS = edition.feedback.submissions;
    const sentiment = edition.feedback.sentiment;
    const participationRate = (live?.source === 'live' && live.participation?.rate != null)
        ? live.participation.rate
        : edition.summary.participationRate;

    const active = SCOPES.find((s) => s.id === scope) || SCOPES[0];
    const belowFloor = scope !== 'org' && active.responses < MIN_GROUP_SIZE;
    const smallGroup = scope !== 'org' && active.responses >= MIN_GROUP_SIZE && active.responses < RECOMMENDED_GROUP_SIZE;

    const visible = useMemo(() => {
        if (scope === 'org') return SUBMISSIONS;
        if (belowFloor) return [];
        return SUBMISSIONS.filter((s) => s.scope === scope);
    }, [scope, belowFloor, SUBMISSIONS]);

    const suppressedGroups = SCOPES.filter((s) => s.id !== 'org' && s.responses < MIN_GROUP_SIZE);

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 no-print">

            {/* Scope selector + privacy floor banner */}
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <Users size={18} className="text-[var(--brand-blue)]" />
                        <span className="text-sm font-bold text-[var(--brand-deep)]">Reporting scope</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {SCOPES.map((s) => {
                            const suppressed = s.id !== 'org' && s.responses < MIN_GROUP_SIZE;
                            const isActive = scope === s.id;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => !suppressed && setScope(s.id)}
                                    disabled={suppressed}
                                    title={suppressed ? `Suppressed — below k ≥ ${MIN_GROUP_SIZE}` : ''}
                                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                                        suppressed
                                            ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
                                            : isActive
                                                ? 'bg-[var(--brand-deep)] text-white border-[var(--brand-deep)] shadow-sm'
                                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                    }`}
                                >
                                    {suppressed && <Lock size={11} />}
                                    {s.label}
                                    <span className={`text-[10px] font-bold ${isActive ? 'text-blue-200' : 'text-slate-400'}`}>
                                        {s.responses}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed">
                    <ShieldCheck size={14} className="text-[var(--brand-deep)] shrink-0 mt-0.5" />
                    <span>
                        Reporting defaults to the whole organization. A group only becomes selectable once it clears the
                        minimum size of <strong className="text-slate-700">k ≥ {MIN_GROUP_SIZE}</strong> ({RECOMMENDED_GROUP_SIZE} recommended for larger employers).
                        {suppressedGroups.length > 0 && (
                            <> {suppressedGroups.length} group{suppressedGroups.length > 1 ? 's are' : ' is'} below the floor and rolled into the organization-wide view.</>
                        )}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* AI Sentiment Summary (Left Col) */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="bg-[var(--brand-deep)] p-7 rounded-3xl  shadow-xl relative overflow-hidden text-white">
                        <div className="absolute top-0 right-0 p-4 opacity-10"><MessageSquare size={120} /></div>
                        <div className="flex items-center justify-between mb-2 relative z-10">
                            <h2 className="text-xl font-black flex items-center gap-2">
                                <Sparkles size={20} className="text-[var(--brand-blue)]" /> AI Sentiment
                            </h2>
                            <ConfidenceTag level="Leading" />
                        </div>
                        <p className="text-sm text-slate-300 font-medium mb-8 relative z-10">
                            Directional NLP themes across the {active.label.toLowerCase()} pool · {active.responses} responses
                        </p>

                        <div className="space-y-5 relative z-10">
                            <div className="bg-white/10 p-4 rounded-xl border border-white/10">
                                <p className="text-sm leading-relaxed text-slate-200">
                                    <strong className="text-white block mb-1">Trending positive:</strong>
                                    {sentiment.positiveSummary}
                                </p>
                            </div>
                            <div className="bg-white/15 p-4 rounded-xl border border-white/20">
                                <p className="text-sm leading-relaxed text-slate-200">
                                    <strong className="text-[var(--brand-blue)] block mb-1">Emerging theme:</strong>
                                    {sentiment.emergingTheme}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-7 rounded-3xl border border-slate-200 shadow-sm">
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider mb-6">Pulse Metrics</h3>
                        <div className="space-y-6">
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-500 font-medium">Psychological safety</span>
                                    <span className="font-bold text-slate-800">88/100</span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2.5">
                                    <div className="bg-emerald-500 h-2.5 rounded-full" style={{ width: '88%' }}></div>
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between text-sm mb-2">
                                    <span className="text-slate-500 font-medium">Participation rate</span>
                                    <span className="font-bold text-slate-800">{participationRate}%</span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2.5">
                                    <div className="bg-[var(--brand-blue)] h-2.5 rounded-full" style={{ width: `${participationRate}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* De-identified Feed (Right Col) */}
                <div className="lg:col-span-2 bg-white p-7 md:p-10 rounded-3xl border border-slate-200 shadow-sm flex flex-col">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                        <div>
                            <h2 className="text-xl font-black text-[var(--brand-deep)] mb-2 flex items-center gap-2">
                                <EyeOff size={24} className="text-slate-400" /> De-identified Submission Feed
                            </h2>
                            <p className="text-sm text-slate-500 font-medium">
                                Names, identifiers and group attribution are removed. Quotes are pooled at the selected scope.
                            </p>
                        </div>
                        <span className="bg-blue-50 text-[var(--brand-deep)] text-xs font-black px-4 py-2 rounded-lg uppercase tracking-widest border border-blue-100 shadow-sm flex items-center gap-1.5">
                            <ShieldCheck size={14} /> k ≥ {MIN_GROUP_SIZE} enforced
                        </span>
                    </div>

                    {smallGroup && (
                        <div className="mb-5 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2.5 leading-relaxed">
                            <Info size={14} className="shrink-0 mt-0.5 text-amber-600" />
                            <span>
                                This group clears the floor but sits below the recommended size of {RECOMMENDED_GROUP_SIZE}. Read the themes directionally.
                            </span>
                        </div>
                    )}

                    {belowFloor ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                            <Lock size={28} className="text-slate-300 mb-4" />
                            <p className="text-sm font-bold text-slate-600 mb-1">Suppressed to protect anonymity</p>
                            <p className="text-xs text-slate-400 font-medium max-w-sm leading-relaxed">
                                This group has fewer than {MIN_GROUP_SIZE} responses, so its submissions aren&apos;t shown individually.
                                They&apos;re still counted in the organization-wide view.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4 flex-1">
                            {visible.map((fb) => (
                                <div key={fb.id} className="p-6 rounded-2xl border border-slate-100 bg-slate-50 hover:bg-slate-100/50 transition-colors">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-bold text-[var(--brand-deep)] bg-white px-3 py-1.5 rounded-md border border-slate-200 shadow-sm">
                                                {fb.theme}
                                            </span>
                                            <span className={`w-2 h-2 rounded-full ${fb.sentiment === 'risk' ? 'bg-amber-500' : 'bg-emerald-500'}`}></span>
                                        </div>
                                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">{fb.time}</span>
                                    </div>
                                    <p className="text-slate-700 text-base leading-relaxed font-medium">
                                        &ldquo;{fb.text}&rdquo;
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnonymousFeedback;
